import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Handshake, Clock, UserCheck, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal, SectionLabel, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_CLASS =
  "rounded-none bg-transparent border-white/15 text-bone placeholder:text-smoke/60 focus-visible:ring-1 focus-visible:ring-ember focus-visible:ring-offset-0 focus-visible:border-ember transition-colors duration-300";

const POINTS = [
  { icon: Handshake, text: "Nothing upfront for your vendors" },
  { icon: Clock, text: "Fast turnarounds for campaign deadlines" },
  { icon: UserCheck, text: "One painter, one point of contact" },
];

export default function AgentStrip() {
  const [form, setForm] = useState({ name: "", agency: "", contact: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/agent-enquiries`, form);
      setSent(true);
      toast.success("Partnership enquiry sent — Craig will be in touch.");
    } catch (err) {
      toast.error("Something went wrong — please email afterpaint@hotmail.com instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="agents"
      data-testid="agent-strip-section"
      className="bg-ash border-y border-line py-20 md:py-28 px-6 md:px-12 lg:px-24"
    >
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7">
          <Reveal>
            <SectionLabel>For Real Estate Agents</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.05]">
              Offer your vendors{" "}
              <span className="italic text-ember">paint now, pay later.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-smoke font-light leading-relaxed mt-8 max-w-xl text-base md:text-lg">
              Every agent knows the listing that needed "a bit of a tidy-up".
              Hand it to After Paint: we get the home market-ready before the
              photos, and your vendor pays nothing until settlement. Better
              campaigns, stronger offers, happier sellers.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <button
              data-testid="agent-partner-cta"
              onClick={() => {
                setFormOpen(true);
                setTimeout(
                  () => document.querySelector('[data-testid="agent-name-input"]')?.focus(),
                  500
                );
              }}
              className="mt-10 inline-flex items-center gap-3 bg-ember text-coal text-xs uppercase tracking-[0.2em] font-semibold px-9 py-4 rounded-full hover:bg-ember-dark transition-colors duration-300"
            >
              Partner with After Paint
              <ArrowUpRight size={14} strokeWidth={2} />
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-5 lg:pt-16">
          {POINTS.map((p, i) => (
            <Reveal key={p.text} delay={0.15 + i * 0.08}>
              <div className="flex items-center gap-5 py-6 border-t border-line last:border-b">
                <p.icon size={18} strokeWidth={1.25} className="text-ember shrink-0" />
                <p className="text-bone/90 font-light text-base">{p.text}</p>
              </div>
            </Reveal>
          ))}
          <AnimatePresence initial={false}>
            {formOpen && (
              <motion.div
                key="agent-form-wrap"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="overflow-hidden"
              >
                <div className="mt-12 bg-coal border border-line p-8">
              {sent ? (
                <div data-testid="agent-success-panel" className="flex flex-col items-start gap-4 py-4">
                  <span className="flex items-center justify-center w-12 h-12 border border-ember">
                    <Check size={18} className="text-ember" strokeWidth={1.5} />
                  </span>
                  <p className="font-serif font-light text-2xl tracking-tight">Enquiry sent.</p>
                  <p className="text-smoke font-light text-sm leading-relaxed">
                    Thanks — Craig will call you back about partnering up,
                    usually within a day.
                  </p>
                </div>
              ) : (
                <form data-testid="agent-form" onSubmit={handleSubmit} className="space-y-5">
                  <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-ember">
                    Partner enquiry
                  </p>
                  <Input
                    data-testid="agent-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className={FIELD_CLASS}
                  />
                  <Input
                    data-testid="agent-agency-input"
                    required
                    value={form.agency}
                    onChange={(e) => setForm({ ...form, agency: e.target.value })}
                    placeholder="Agency"
                    className={FIELD_CLASS}
                  />
                  <Input
                    data-testid="agent-contact-input"
                    required
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    placeholder="Best phone or email"
                    className={FIELD_CLASS}
                  />
                  <Textarea
                    data-testid="agent-message-input"
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Which patch do you cover? (optional)"
                    className={`${FIELD_CLASS} resize-none`}
                  />
                  <motion.button
                    data-testid="agent-submit-button"
                    whileTap={{ scale: 0.97 }}
                    type="submit"
                    disabled={sending}
                    className="w-full bg-ember text-coal text-xs uppercase tracking-[0.25em] font-semibold py-4 rounded-full hover:bg-ember-dark disabled:opacity-60 transition-colors duration-300"
                  >
                    {sending ? "Sending…" : "Send Partnership Enquiry"}
                  </motion.button>
                </form>
              )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <Reveal delay={0.5}>
            <p className="text-[10px] uppercase tracking-[0.3em] text-smoke mt-8">
              Or call <a href="tel:0410514499" data-testid="agent-phone-link" className="text-ember hover:text-bone transition-colors duration-300">0410 514 499</a> to talk it through
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
